import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConstructionLayout from './ConstructionLayout';
import { fetchProjects } from '../../services/projectService';
import { resolveProjectCardImage } from '../../data/siteVisualsMedia';
import { useModule } from '../../contexts/ModuleContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Folder, TrendingUp, Calendar, CheckCircle2, Search, ArrowRight, MapPin, Grid, List, MoreVertical, Building2, RefreshCw, Plus } from 'lucide-react';
import AddProjectModal from './AddProjectModal';
import { hasScope, SCOPES } from '../../utils/authStorage';
import { Feature } from '../../config/features';

export default function ProjectsPage() {
  const { isDarkMode } = useTheme();
  const canWrite = hasScope(SCOPES.CONSTRUCTION_WRITE);
  const navigate = useNavigate();
  const { setActiveProject } = useModule();
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [sortBy, setSortBy] = useState('Last Updated');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [imageErrors, setImageErrors] = useState({});

  const loadProjects = () => {
    setLoading(true);
    fetchProjects()
      .then((data) => {
        setProjects(
          (data || []).map((p) => {
            const code = p.code || p.id;
            return {
              ...p,
              code,
              imageUrl: resolveProjectCardImage(code, p.imageUrl),
            };
          })
        );
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load projects from backend:', err);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadProjects();
  }, []);

  // Filter projects by search query
  const filteredProjects = projects.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort projects based on selected option
  const sortedProjects = [...filteredProjects].sort((a, b) => {
    if (sortBy.includes('Name')) {
      return a.name.localeCompare(b.name);
    }
    if (sortBy.includes('Status')) {
      return a.status.localeCompare(b.status);
    }
    // Default: Last Updated, newest first
    return (Date.parse(b.lastUpdatedAt) || 0) - (Date.parse(a.lastUpdatedAt) || 0);
  });

  const handleSelectProject = (proj) => {
    const code = proj.code || proj.id;
    setActiveProject({
      id: code,
      code,
      numericId: proj.numericId,
      name: proj.name,
      location: proj.location,
      lastSurvey: proj.lastSurveyDate ? proj.lastSurvey : null,
      imageUrl: resolveProjectCardImage(code, proj.imageUrl),
    });
    navigate('/construction/visuals');
  };

  const activeCount = projects.filter((p) => (p.status || '').toLowerCase() === 'active').length;
  const upcomingCount = projects.filter((p) => (p.status || '').toLowerCase() === 'upcoming').length;
  const completedCount = projects.filter((p) => (p.status || '').toLowerCase() === 'completed').length;

  const statCards = [
    { label: 'Total Projects', value: projects.length, icon: Folder, color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.15)' },
    { label: 'Active Projects', value: activeCount, icon: TrendingUp, color: '#10B981', bg: 'rgba(16, 185, 129, 0.15)' },
    { label: 'Upcoming', value: upcomingCount, icon: Calendar, color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.15)' },
    { label: 'Completed', value: completedCount, icon: CheckCircle2, color: '#A855F7', bg: 'rgba(168, 85, 247, 0.15)' },
  ];

  return (
    <ConstructionLayout title="Projects" showProjectSelector={false}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: isDarkMode ? '#F3F4F6' : '#111827', margin: 0 }}>Projects</h2>
            <p style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280', margin: '4px 0 0 0' }}>
              Select a project to view intelligence and analytics
            </p>
          </div>

          {canWrite && <button
            onClick={() => setIsAddModalOpen(true)}
            style={{
              backgroundColor: '#0082F8',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 18px',
              fontSize: '13px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0, 130, 248, 0.3)',
              transition: 'background-color 0.15s',
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#006ed6')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#0082F8')}
          >
            <Plus size={16} />
            Add Project
          </button>}
        </div>

        {/* 4 Stat Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
          {statCards.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <div
                key={idx}
                style={{
                  backgroundColor: isDarkMode ? '#111622' : '#FFFFFF',
                  border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`,
                  borderRadius: '12px',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                }}
              >
                <div style={{ width: '42px', height: '42px', borderRadius: '10px', backgroundColor: stat.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={20} color={stat.color} />
                </div>
                <div>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: isDarkMode ? '#F3F4F6' : '#111827' }}>{stat.value}</div>
                  <div style={{ fontSize: '12px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>{stat.label}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Toolbar: Search, Sort & View Mode Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: isDarkMode ? '#111622' : '#FFFFFF',
              border: `1px solid ${isDarkMode ? '#1E2433' : '#D1D5DB'}`,
              borderRadius: '8px',
              padding: '8px 14px',
              width: '320px',
            }}
          >
            <Search size={16} color="#9CA3AF" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                border: 'none',
                background: 'none',
                color: isDarkMode ? '#F3F4F6' : '#111827',
                outline: 'none',
                fontSize: '13px',
                width: '100%',
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                backgroundColor: isDarkMode ? '#111622' : '#FFFFFF',
                border: `1px solid ${isDarkMode ? '#1E2433' : '#D1D5DB'}`,
                borderRadius: '8px',
                padding: '8px 12px',
                color: isDarkMode ? '#F3F4F6' : '#111827',
                fontSize: '13px',
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="Last Updated">Sort by: Last Updated</option>
              <option value="Name">Sort by: Name</option>
              <option value="Status">Sort by: Status</option>
            </select>

            {/* Grid vs List Toggle Buttons */}
            <div style={{ display: 'flex', gap: '2px', backgroundColor: isDarkMode ? '#111622' : '#E5E7EB', padding: '3px', borderRadius: '8px' }}>
              <button
                onClick={() => setViewMode('grid')}
                title="Grid view"
                style={{
                  border: 'none',
                  backgroundColor: viewMode === 'grid' ? (isDarkMode ? '#1E2433' : '#FFF') : 'transparent',
                  padding: '6px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Grid size={16} color={viewMode === 'grid' ? (isDarkMode ? '#F3F4F6' : '#111827') : '#9CA3AF'} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                title="List view"
                style={{
                  border: 'none',
                  backgroundColor: viewMode === 'list' ? (isDarkMode ? '#1E2433' : '#FFF') : 'transparent',
                  padding: '6px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <List size={16} color={viewMode === 'list' ? (isDarkMode ? '#F3F4F6' : '#111827') : '#9CA3AF'} />
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: isDarkMode ? '#9CA3AF' : '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
            <RefreshCw className="animate-spin" size={20} /> Loading projects from backend database...
          </div>
        ) : sortedProjects.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: isDarkMode ? '#9CA3AF' : '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <Building2 size={36} color={isDarkMode ? '#4B5563' : '#9CA3AF'} />
            <div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: isDarkMode ? '#F3F4F6' : '#111827' }}>
                {searchQuery ? 'No matching projects found.' : 'No projects found in database.'}
              </div>
              <div style={{ fontSize: '13px', marginTop: '4px' }}>
                {searchQuery ? 'Try adjusting your search query.' : 'Get started by creating your first construction project.'}
              </div>
            </div>
            {!searchQuery && canWrite && (
              <button
                onClick={() => setIsAddModalOpen(true)}
                style={{
                  backgroundColor: '#0082F8',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '9px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  marginTop: '8px',
                }}
              >
                <Plus size={15} /> Add Project
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          /* Grid View Layout */
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
            {sortedProjects.map((proj) => (
              <div
                key={proj.id}
                style={{
                  backgroundColor: isDarkMode ? '#111622' : '#FFFFFF',
                  border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)',
                  transition: 'transform 0.2s',
                }}
              >
                {/* Project Image Banner */}
                <div style={{ position: 'relative', height: '140px', backgroundColor: isDarkMode ? '#161B26' : '#F0F4F8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {proj.imageUrl && !imageErrors[proj.id] ? (
                    <img 
                      src={proj.imageUrl} 
                      alt={proj.name} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                      onError={() => setImageErrors((prev) => ({ ...prev, [proj.id]: true }))}
                    />
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', color: '#0082F8' }}>
                      <Building2 size={36} />
                      <span style={{ fontSize: '11px', fontWeight: 600, color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                        {proj.name}
                      </span>
                    </div>
                  )}
                  <span
                    style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      backgroundColor: proj.status === 'Active' ? '#10B981' : '#3B82F6',
                      color: '#FFF',
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '4px 10px',
                      borderRadius: '20px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {proj.status}
                  </span>
                </div>

                {/* Card Body */}
                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3 style={{ fontSize: '15px', fontWeight: 700, color: isDarkMode ? '#F3F4F6' : '#111827', margin: 0 }}>
                        {proj.name}
                      </h3>
                      <div style={{ fontSize: '12px', color: isDarkMode ? '#9CA3AF' : '#6B7280', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                        <MapPin size={12} />
                        {proj.location}
                      </div>
                    </div>
                    <Feature name="PROJECT_CARD_MENU">
                      <button style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer' }}>
                        <MoreVertical size={16} />
                      </button>
                    </Feature>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px', backgroundColor: isDarkMode ? '#161B26' : '#F9FAFB', padding: '10px', borderRadius: '8px' }}>
                    <div>
                      <span style={{ color: isDarkMode ? '#9CA3AF' : '#6B7280', display: 'block' }}>Last Survey</span>
                      <strong style={{ color: isDarkMode ? '#E5E7EB' : '#1F2937' }}>{proj.lastSurvey}</strong>
                    </div>
                    <div>
                      <span style={{ color: isDarkMode ? '#9CA3AF' : '#6B7280', display: 'block' }}>Last Updated</span>
                      <strong style={{ color: isDarkMode ? '#E5E7EB' : '#1F2937' }}>{proj.lastUpdated}</strong>
                    </div>
                  </div>

                  <button
                    onClick={() => handleSelectProject(proj)}
                    style={{
                      marginTop: 'auto',
                      width: '100%',
                      backgroundColor: isDarkMode ? '#1E2433' : '#EFF6FF',
                      color: '#0082F8',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px',
                      fontSize: '13px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    View Project
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View Layout */
          <div style={{ backgroundColor: isDarkMode ? '#111622' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`, borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                <thead>
                  <tr style={{ backgroundColor: isDarkMode ? '#161B26' : '#F9FAFB', borderBottom: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`, color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                    <th style={{ padding: '12px 16px' }}>Project Name</th>
                    <th style={{ padding: '12px 16px' }}>Location</th>
                    <th style={{ padding: '12px 16px' }}>Status</th>
                    <th style={{ padding: '12px 16px' }}>Last Survey</th>
                    <th style={{ padding: '12px 16px' }}>Last Updated</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedProjects.map((proj) => (
                    <tr
                      key={proj.id}
                      style={{
                        borderBottom: `1px solid ${isDarkMode ? '#1E2433' : '#F3F4F6'}`,
                        color: isDarkMode ? '#E5E7EB' : '#1F2937',
                      }}
                    >
                      <td style={{ padding: '12px 16px', fontWeight: 700 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Building2 size={16} color="#0082F8" />
                          {proj.name}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                          <MapPin size={13} />
                          {proj.location}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span
                          style={{
                            backgroundColor: proj.status === 'Active' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(59, 130, 246, 0.15)',
                            color: proj.status === 'Active' ? '#10B981' : '#3B82F6',
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '4px 10px',
                            borderRadius: '12px',
                          }}
                        >
                          {proj.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>{proj.lastSurvey}</td>
                      <td style={{ padding: '12px 16px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>{proj.lastUpdated}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <button
                          onClick={() => handleSelectProject(proj)}
                          style={{
                            backgroundColor: isDarkMode ? '#1E2433' : '#EFF6FF',
                            color: '#0082F8',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          View <ArrowRight size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <AddProjectModal
        isOpen={canWrite && isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onProjectAdded={(newProj) => {
          loadProjects();
        }}
      />
    </ConstructionLayout>
  );
}
